import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { usePermissions, MODULE_ROUTE_PREFIXES } from '@/contexts/PermissionsContext';
import ModuleGuard from '@/components/ModuleGuard';
import AssistantButton from '@/components/AssistantButton';
import ScanFactureButton from '@/components/ScanFactureButton';
import { ActivityButton } from '@/components/ActivityPanel';
import {
  LayoutDashboard,
  Map,
  Users,
  Wallet,
  FileCheck,
  Bot,
  Settings,
  LogOut,
  Leaf,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Menu,
  Tractor,
  Sparkles,
  Brain,
  Building2,
  CheckCircle2,
  Crown,
  UsersRound,
  UserCog,
  Plug,
  CreditCard,
  Calendar,
  Calculator,
  Package,
  FlaskConical,
  Flame,
  Droplets,
  ShoppingCart,
  MessageCircle,
  BarChart3,
  ClipboardList,
  Mic,
  Store,
  HelpCircle,
  BookOpen,
  AlertTriangle,
  Euro,
  Shield,
  FileText,
  HardDrive,
  Scale,
  Thermometer,
  Truck,
  Video,
  RefreshCw,
  MapPin,
  ClipboardCheck,
  Bug,
  Wine,
  Heart,
  GraduationCap,
  Home,
  Clock,
  FolderOpen,
  Globe,
  Target,
  Activity,
  Search,
  Navigation,
  Camera,
  Map as MapIcon,
  Briefcase,
  Mail,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { MobileMoreMenu } from '@/components/MobileMoreMenu';
import { NotificationsDropdown } from '@/components/NotificationsDropdown';
import { SupportChatbot } from '@/components/SupportChatbot';

// Structure de navigation SIMPLIFIÉE et FUSIONNÉE - Réorganisation demandée par Fred
const navItems = [
  // 1. Tableau de bord principal
  { to: '/', icon: LayoutDashboard, label: 'Tableau de bord' },
  { to: '/vue-groupe', icon: Store, label: 'Vue groupe', hideForOwner: true },

  // 1.5 Mes tâches (universel - tous utilisateurs)
  { to: '/mes-taches', icon: Activity, label: 'Mes tâches' },
  { to: '/parametres/google?tab=gmail', icon: Mail, label: 'Boîte mail' },
  
  // 1bis. Espace Conseiller Multi-Clients (uniquement C3 Viti)
  { to: '/conseiller', icon: Shield, label: 'Espace Conseiller', restrictedSocietes: ['c3viti-001df427'] },
  
  // 2. Équipe & RH (fusion Equipe + RH + Agents IA) - Masqué si show_rh=false
  { 
    to: '/team', 
    icon: Users, 
    label: 'Équipe & RH',
    moduleKey: 'rh',
    submenu: [
      { to: '/team', label: 'Collaborateurs', moduleKey: 'rh' },
      { to: '/rh-social', label: 'RH / Paie', icon: UserCog, moduleKey: 'rh' },
      { to: '/alertes-heures', label: 'Alertes Saisie Heures', icon: Clock, moduleKey: 'rh' },
      { to: '/agents/config', label: 'Mon Équipe IA', icon: Bot },
      // Agents spéciaux (seulement pour Fred)
      { to: '/agent-casia', label: 'Agent CASIA', icon: FlaskConical, ownerOnly: true, societeNom: 'casia' },
      { to: '/agent-distillateur', label: 'Agent Distillateur', icon: Flame, ownerOnly: true, societeNom: 'distill' },
    ]
  },
  
  // 3. Exploitation - allégé (carte tout-en-un, GPS intégré à la carte)
  { 
    to: '/parcellaire', 
    icon: Tractor, 
    label: 'Exploitation',
    moduleKey: 'parcellaire',
    submenu: [
      { to: '/parcellaire', label: 'Carte tout-en-un (parcelles, GPS, interventions)', icon: Map, moduleKey: 'parcellaire' },
      { to: '/journal-interventions', label: 'Journal & Interventions', icon: BookOpen, moduleKey: 'interventions' },
      { to: '/journee-chauffeur', label: 'Journée chauffeur (IA)', icon: Sparkles, moduleKey: 'interventions' },
      { to: '/materiel', label: 'Matériel', icon: Tractor, moduleKey: 'materiel' },
      { to: '/registre-phyto', label: 'Registre Phyto', icon: Droplets, moduleKey: 'registre_phyto' },
      { to: '/fertilisation', label: 'Fertilisation IA', icon: Leaf, moduleKey: 'fertilisation' },
      { to: '/irrigation', label: 'Irrigation', icon: Droplets, moduleKey: 'irrigation' },
      { to: '/tarifs-eta', label: 'Grilles Tarifaires', icon: CreditCard, moduleKey: 'tarifs_eta' },
    ]
  },
  
  // 4. Comptabilité (stocks unifiés produits/phyto/atelier)
  { 
    to: '/comptabilite', 
    icon: Calculator, 
    label: 'Comptabilité',
    moduleKey: 'comptabilite',
    submenu: [
      { to: '/comptabilite', label: 'Comptabilité' },
      { to: '/comptabilite/assistant', label: 'Assistant Comptable', icon: Bot },
      { to: '/gestion-commerciale', label: 'Devis & Factures', icon: FileText },
      { to: '/facturation-electronique', label: 'Facturation Électronique', icon: FileText },
      { to: '/stocks', label: 'Stocks (produits, phyto, atelier)', icon: Package, moduleKey: 'stocks' },
      { to: '/finance', label: 'Finances & Banque', icon: Wallet },
      { to: '/geofolia/analyse', label: 'Analyse Coûts', icon: BarChart3, moduleKey: 'geofolia' },
      { to: '/comptabilite/zrr-export', label: 'ZRR/FRR & Export', icon: MapPin },
    ]
  },
  
  // 5. Commerce & Marketing (avec Studio IA & Site Web + Calendrier Publication fusionnés)
  { 
    to: '/crm', 
    icon: Store, 
    label: 'Commerce & Marketing',
    submenu: [
      { to: '/crm', label: 'CRM / Clients', icon: Users },
      { to: '/prospection-auto', label: 'Campagnes de prospection', icon: Mail },
      { to: '/carte-clients', label: 'Carte des Clients', icon: MapIcon },
      { to: '/studio-ia', label: 'Studio IA & Site Web', icon: Sparkles },
      { to: '/video-hub', label: 'Hub Video', icon: Video },
      { to: '/videos-virales', label: 'Vidéos virales', icon: Sparkles },
      { to: '/crm-vente-logiciel', label: 'Vente Bureau Agricole', icon: CreditCard, fredDevOnly: true },
      { to: '/assistant-visio', label: 'Enregistrement de reunion', icon: Video },
    ]
  },
  
  // 6. Assistant Bureau Agricole
  { 
    to: '/assistante', 
    icon: Mic, 
    label: 'Assistant Bureau Agricole',
    submenu: [
      { to: '/assistante', label: 'Assistant IA', icon: Mic },
      { to: '/assistant-terrain', label: 'Assistant Terrain (vocal)', icon: Mic },
      { to: '/taches-ia', label: 'Rappels IA', icon: Sparkles },
      { to: '/drive-monitor', label: 'Drive Monitor', icon: FolderOpen },
    ]
  },
  
  // 7b. Alertes PAC & Rappels - MASQUÉ du menu principal, accessible via Réglementaire
  // { to: '/alertes-pac', icon: AlertTriangle, label: 'Alertes & Échéances' },
  
  // 9. Module Expéditeur (Station fruitière) - MODULE OPTIONNEL pour les clients
  { 
    to: '/expediteur', 
    icon: Package, 
    label: 'Expéditeur F&L',
    moduleKey: 'expediteur',
    hideForOwner: true, // Masqué pour Fred (pas besoin)
    submenu: [
      { to: '/expediteur', label: 'Tableau de bord' },
      { to: '/expediteur?tab=fournisseurs', label: 'Fournisseurs', icon: Users },
      { to: '/expediteur?tab=receptions', label: 'Réceptions', icon: Package },
      { to: '/expediteur?tab=calibrage', label: 'Calibrage', icon: Scale },
      { to: '/expediteur?tab=frigos', label: 'Chambres Froides', icon: Thermometer },
      { to: '/expediteur?tab=tournees', label: 'Tournées', icon: Truck },
    ]
  },
  
  // 10. Élevage - MODULE CLIENT UNIQUEMENT (masqué pour Fred et par défaut)
  { 
    to: '/elevage', 
    icon: UsersRound, 
    label: 'Élevage',
    moduleKey: 'elevage',
    hideForOwner: true,
    hidden: true,
    submenu: [
      { to: '/elevage', label: 'Tableau de bord élevage' },
      { to: '/elevage/animaux', label: 'Mes animaux' },
      { to: '/elevage/chevaux', label: 'Chevaux', icon: UsersRound },
      { to: '/elevage/alimentation', label: 'Alimentation' },
      { to: '/elevage/sante', label: 'Santé & Vétérinaire' },
      { to: '/elevage/reproduction', label: 'Reproduction & Génétique' },
      { to: '/elevage/pension', label: 'Pension & Centre équestre' },
      { to: '/elevage/commerce', label: 'Commerce & Opportunités' },
      { to: '/agent-eleveur', label: 'Agent IA Éleveur', icon: Bot },
    ]
  },

  // 10b. Cave & Vinification - MODULE CLIENT UNIQUEMENT (masqué pour Fred)
  { 
    to: '/cave', 
    icon: Wine, 
    label: 'Cave & Vinification',
    moduleKey: 'cave',
    hideForOwner: true,
    submenu: [
      { to: '/cave', label: 'Tableau de bord cave' },
      { to: '/cave?tab=cuves', label: 'Gestion des cuves' },
      { to: '/cave?tab=intrants', label: 'Intrants vinification' },
      { to: '/cave?tab=matieres', label: 'Matières sèches' },
      { to: '/cave?tab=lots', label: 'Lots & Traçabilité' },
      { to: '/cave?tab=registre', label: 'Registre de cave & assemblages' },
      { to: '/caveau', label: 'Caveau & caisse' },
      { to: '/etiquetage-vin', label: 'Étiquetage réglementaire' },
      { to: '/cave?tab=alertes', label: 'Alertes IA', icon: Bot },
    ]
  },

  // 10c. Module CHEVAUX UNIFIÉ - Regroupe Pension, Élevage, Débourrage, Attelage
  { 
    to: '/chevaux', 
    icon: Heart, 
    label: 'Chevaux',
    moduleKey: 'pension', // Activé si pension OU elevage_equin OU debourrage OU attelage
    submenu: [
      { to: '/pension', label: 'Pension & Tableau de bord', icon: LayoutDashboard },
      { to: '/bot-equin', label: 'Bot Expert Équin', icon: Bot },
      { to: '/debourrage', label: 'Débourrage', icon: Sparkles, moduleKey: 'debourrage' },
      { to: '/attelage', label: 'Attelage', icon: Truck, moduleKey: 'attelage' },
      { to: '/formations-equines', label: 'Formations', icon: GraduationCap, moduleKey: 'formation' },
      { to: '/elevage', label: 'Élevage & Reproduction', icon: Heart },
      { to: '/chevaux?tab=boxes', label: 'Boxes & Prés', icon: MapPin },
      { to: '/chevaux?tab=crm', label: 'CRM Propriétaires', icon: Users },
      { to: '/chevaux?tab=facturation', label: 'Facturation', icon: CreditCard },
    ]
  },
  
  // 11. Paramètres (fusion Workspace + Intégrations + Tarifs)
  { 
    to: '/parametres', 
    icon: Settings, 
    label: 'Administratif & Paramètres',
    submenu: [
      { to: '/valentine-mails', label: 'Valentine Mails (réponses à valider)', icon: Mail, ownerOnly: true },
      { to: '/scan-courrier', label: 'Scan Courrier (IA)', icon: Camera, moduleKey: 'crm' },
      { to: '/contrats-universels', label: 'Contrats & Signatures', icon: FileText },
      { to: '/reglementaire', label: 'Réglementaire & Assurances', icon: FileCheck, moduleKey: 'reglementaire' },
      { to: '/workspace', label: 'Mon Workspace & Coordonnées', icon: Building2 },
      { to: '/integrations', label: 'Intégrations (Gmail, Geofolia…)', icon: Plug },
      { to: '/billing', label: 'Abonnement', icon: CreditCard },
    ]
  },
  
  // 12. Aide & Support
  { 
    to: '/aide', 
    icon: HelpCircle, 
    label: 'Aide & Support',
    submenu: [
      { to: '/aide', label: 'Guide d\'utilisation', icon: HelpCircle },
      { to: '/support', label: 'Signaler un problème', icon: Bug },
    ]
  },

  // 13. Dashboard Santé Système - Frédéric Développement uniquement
  { 
    to: '/sante-systeme', 
    icon: Activity, 
    label: 'Santé Système',
    restrictedSocietes: ['d3db4e3f-c6ac-471d-97a8-e58fd382f0cc'],
  },
  
  // Items spéciaux (masqués du menu principal, accessibles par URL)
  { to: '/pitch-deck', icon: Sparkles, label: 'Pitch Deck Valentine', hidden: true },
  { to: '/mon-suivi-gps', icon: Navigation, label: 'Mon suivi GPS (ancien)', hidden: true },
  { to: '/vente-bureau-agricole', icon: ShoppingCart, label: 'Vente Logiciel', hidden: true, societeIds: ['729fbf20-eccd-4bde-9ef3-e5a03a34a27f'] },
  { to: '/creator', icon: Sparkles, label: 'Creator', hidden: true },
  { to: '/agent-futur-projet', icon: Sparkles, label: 'Agent Futur Projet', hidden: true },
  { to: '/ecommerce', icon: ShoppingCart, label: 'E-commerce', hidden: true },
  { to: '/vente-sites', icon: Store, label: 'Sites Web IA', hidden: true },
  { to: '/calendrier-publication', icon: Calendar, label: 'Calendrier Publication', hidden: true },
  { to: '/bot-interventions', icon: Bot, label: 'Bot Interventions', hidden: true },
  { to: '/assistante-direction', icon: Package, label: 'Import Stocks', hidden: true },
  
  // Admin Développeur - Frédéric uniquement (contrôle du site)
  { 
    to: '/admin-dev', 
    icon: Shield, 
    label: 'Admin Développeur',
    adminOnly: true,
    societeIds: ['729fbf20-eccd-4bde-9ef3-e5a03a34a27f'],
    submenu: [
      { to: '/security', label: 'Sécurité & Accès', icon: Shield },
      { to: '/prospection', label: 'Suivi Prospection', icon: Users },
      { to: '/enrichir-campagnes', label: 'Enrichir Campagnes', icon: Search },
      { to: '/admin/backups', label: 'Sauvegardes', icon: HardDrive },
      { to: '/admin/users', label: 'Gestion Utilisateurs', icon: UserCog },
    ]
  },
];

// Navigation Item with Submenu Support
const NavItemWithSubmenu = ({ item, collapsed, activeSociete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  
  // Check if current path matches this item or its submenu
  const isActive = location.pathname === item.to || 
    (item.submenu && item.submenu.some(sub => location.pathname === sub.to));
  
  // If no submenu, render simple NavLink
  if (!item.submenu) {
    return (
      <li>
        <NavLink
          to={item.to}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2 mx-2 rounded-lg transition-colors',
              isActive
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              collapsed && 'justify-center px-2'
            )
          }
          data-testid={`nav-${item.to.replace(/\//g, '-').slice(1) || 'home'}`}
        >
          <item.icon className={cn('w-5 h-5 flex-shrink-0', item.highlight && 'text-amber-500')} />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </NavLink>
      </li>
    );
  }
  
  // With submenu - render expandable section
  return (
    <li>
      {/* Parent item - clickable to toggle or navigate */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center gap-3 px-3 py-2 mx-2 rounded-lg transition-colors',
          isActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          collapsed && 'justify-center px-2'
        )}
        data-testid={`nav-${item.to.replace(/\//g, '-').slice(1)}-toggle`}
      >
        <item.icon className={cn('w-5 h-5 flex-shrink-0', item.highlight && 'text-amber-500')} />
        {!collapsed && (
          <>
            <span className="truncate flex-1 text-left">{item.label}</span>
            <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
          </>
        )}
      </button>
      
      {/* Submenu items */}
      {!collapsed && isOpen && (
        <ul className="mt-1 ml-4 space-y-1 border-l-2 border-border pl-2">
          {item.submenu
            .filter(subItem => {
              // Filtrer les éléments fredDevOnly si on n'est pas sur Frédéric Développement
              if (subItem.fredDevOnly) {
                // Vérifier si la société active est "Frédéric Développement"
                const isFredDev = activeSociete?.nom?.toLowerCase().includes('frédéric') || 
                                  activeSociete?.nom?.toLowerCase().includes('frederic') ||
                                  activeSociete?.nom?.toLowerCase().includes('développement');
                return isFredDev;
              }
              return true;
            })
            .map((subItem) => (
            <li key={subItem.to}>
              <NavLink
                to={subItem.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )
                }
                data-testid={`nav-${subItem.to.replace(/\//g, '-').slice(1)}`}
              >
                {subItem.icon && <subItem.icon className="w-4 h-4 flex-shrink-0" />}
                <span className="truncate">{subItem.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
      
      {/* Collapsed mode - show submenu in dropdown */}
      {collapsed && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="sr-only">Open submenu</button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-48">
            <DropdownMenuLabel>{item.label}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {item.submenu.map((subItem) => (
              <DropdownMenuItem key={subItem.to} asChild>
                <NavLink to={subItem.to} className="flex items-center gap-2 cursor-pointer">
                  {subItem.icon && <subItem.icon className="w-4 h-4" />}
                  <span>{subItem.label}</span>
                </NavLink>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  );
};

// Société Selector Component
const SocieteSelector = ({ collapsed }) => {
  const { tenant, societes, activeSociete, switchSociete, loading } = useWorkspace();

  // Debug logs
  console.log('[SocieteSelector] tenant:', tenant);
  console.log('[SocieteSelector] societes:', societes?.length);
  console.log('[SocieteSelector] loading:', loading);

  // Afficher un placeholder pendant le chargement
  if (loading) {
    return (
      <div className={cn(
        'animate-pulse rounded-lg bg-muted',
        collapsed ? 'w-10 h-10' : 'w-full h-14'
      )} />
    );
  }

  // Ne pas afficher si pas de sociétés ET pas de tenant
  if (!societes || societes.length === 0) {
    console.log('[SocieteSelector] Pas de sociétés, return null');
    return null;
  }

  if (collapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button 
            className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors"
            data-testid="societe-selector-collapsed"
          >
            <Building2 className="w-5 h-5 text-primary" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel>{tenant.nom}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {societes.map((soc) => (
            <DropdownMenuItem
              key={soc.id}
              onClick={() => switchSociete(soc.id)}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                <span>{soc.nom}</span>
              </div>
              {activeSociete?.id === soc.id && (
                <CheckCircle2 className="w-4 h-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className="w-full px-3 py-2 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors text-left flex items-center justify-between border border-primary/20"
          data-testid="societe-selector"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">
                {activeSociete?.nom || 'Sélectionner'}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {activeSociete?.forme_juridique}
              </p>
            </div>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-500" />
          {tenant.branding?.nom_affiche || tenant.nom}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {societes.map((soc) => (
          <DropdownMenuItem
            key={soc.id}
            onClick={() => switchSociete(soc.id)}
            className="flex items-center justify-between cursor-pointer"
            data-testid={`select-societe-${soc.id}`}
          >
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <div>
                <p className="font-medium">{soc.nom}</p>
                <p className="text-xs text-muted-foreground">{soc.forme_juridique}</p>
              </div>
            </div>
            {activeSociete?.id === soc.id && (
              <CheckCircle2 className="w-4 h-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Sidebar = ({ collapsed, setCollapsed }) => {
  const { user, logout } = useAuth();
  const { tenant, activeSociete } = useWorkspace();
  const { isPathBlocked, modulesInterdits } = usePermissions();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get branding colors
  const primaryColor = tenant?.branding?.couleur_primaire || '#22c55e';
  
  // Identification du propriétaire (Fred) pour les fonctionnalités exclusives
  // Utiliser l'email comme méthode fiable de vérification
  const OWNER_EMAILS = ['fredrancelli@gmail.com', 'fred@bureauagricole.fr'];
  const isOwner = OWNER_EMAILS.includes(user?.email?.toLowerCase());
  
  // Filter nav items based on tenant config and various restrictions
  // Récupérer les modules actifs/désactivés de la société
  const modulesActifs = activeSociete?.modules_actifs || [];
  const modulesDesactives = activeSociete?.modules_desactives || [];
  const societeConfig = activeSociete?.config || {};
  
  const filteredNavItems = navItems.filter(item => {
    // Items cachés du menu
    if (item.hidden) return false;

    // Modules interdits par admin (acces_collaborateurs)
    if (item.to && isPathBlocked(item.to)) return false;
    
    // Items réservés UNIQUEMENT au propriétaire (ex: Élevage pour démos)
    if (item.ownerOnly && !isOwner) return false;
    
    // Items masqués pour le propriétaire (ex: Expéditeur - Fred n'en a pas besoin)
    if (item.hideForOwner && isOwner) return false;
    
    // Items restreints à certaines sociétés (ex: Espace Conseiller)
    if (item.restrictedSocietes && item.restrictedSocietes.length > 0) {
      const currentSocieteId = activeSociete?.id || localStorage.getItem('activeSocieteId') || '';
      if (!item.restrictedSocietes.includes(currentSocieteId)) return false;
    }
    
    // Items admin uniquement - vérifier si l'utilisateur est admin ET dans le bon workspace
    if (item.adminOnly) {
      const isAdmin = user?.role === 'admin' || user?.role === 'dirigeant' || isOwner;
      if (!isAdmin) return false;
      
      // Si restriction par société, vérifier
      if (item.societeIds && item.societeIds.length > 0) {
        return activeSociete && item.societeIds.includes(activeSociete.id);
      }
    }
    
    // Si l'item a une restriction societeIds, vérifier si la société active est dans la liste
    if (item.societeIds && item.societeIds.length > 0 && !item.adminOnly) {
      return activeSociete && item.societeIds.includes(activeSociete.id);
    }
    
    // ===== FILTRAGE PAR MODULES ACTIFS/DÉSACTIVÉS DE LA SOCIÉTÉ =====
    // Si la société a des modules désactivés, vérifier
    if (modulesDesactives.length > 0 && item.moduleKey) {
      if (modulesDesactives.includes(item.moduleKey)) return false;
    }
    
    // Vérifier aussi la config de la société (show_materiel, show_stocks, etc.)
    // Les flags show_* sont directement sur activeSociete, pas dans config
    if (activeSociete?.show_materiel === false && (item.to === '/materiel' || item.moduleKey === 'materiel')) return false;
    if (activeSociete?.show_stocks === false && (item.to === '/stocks' || item.moduleKey === 'stocks')) return false;
    if (activeSociete?.show_elevage === false && (item.to === '/elevage' || item.moduleKey === 'elevage')) return false;
    if (activeSociete?.show_expediteur === false && (item.to === '/expediteur' || item.moduleKey === 'expediteur')) return false;
    if (activeSociete?.show_rh === false && item.moduleKey === 'rh') return false;
    if (activeSociete?.show_comptabilite === false && item.moduleKey === 'comptabilite') return false;
    
    // Filtrage strict pour Expéditeur F&L - uniquement si explicitement activé
    if (item.moduleKey === 'expediteur' || item.to?.includes('/expediteur')) {
      if (activeSociete?.show_expediteur !== true && !modulesActifs.includes('expediteur')) return false;
    }
    
    // Filtrage pour Commerce & Marketing - uniquement si type commerce ou modules activés
    if (item.moduleKey === 'commerce' || item.to?.includes('/commerce') || item.to?.includes('/marketing')) {
      const isCommerce = activeSociete?.type === 'commerce' || 
                         activeSociete?.show_ventes === true ||
                         modulesActifs.includes('commerce') ||
                         modulesActifs.includes('marketing') ||
                         modulesActifs.includes('ventes');
      // Ne pas filtrer si c'est un type commerce ou si explicitement activé
    }
    
    // Module Cave - visible uniquement si type viticulture ou module cave activé
    if (item.moduleKey === 'cave') {
      const isViticole = activeSociete?.type === 'viticulture' || modulesActifs.includes('cave');
      if (!isViticole) return false;
    }

    // Module Pension - visible uniquement si type equestre/elevage ou module pension activé
    if (item.moduleKey === 'pension') {
      const isPension = activeSociete?.type === 'equestre' || 
                        activeSociete?.type === 'elevage' ||
                        activeSociete?.show_pension === true ||
                        modulesActifs.includes('pension');
      if (!isPension) return false;
    }

    // Module Élevage Équin - visible uniquement si type equestre ou module activé
    if (item.moduleKey === 'elevage_equin') {
      const isElevageEquin = activeSociete?.type === 'equestre' || 
                            activeSociete?.show_elevage === true ||
                            modulesActifs.includes('elevage') ||
                            modulesActifs.includes('elevage_equin');
      if (!isElevageEquin) return false;
    }

    // Module Formation - visible uniquement si type equestre ou module activé
    if (item.moduleKey === 'formation') {
      const isFormation = activeSociete?.type === 'equestre' || 
                          activeSociete?.show_formation === true ||
                          modulesActifs.includes('formation');
      if (!isFormation) return false;
    }

    // Module Débourrage - visible uniquement si type equestre ou module activé
    if (item.moduleKey === 'debourrage') {
      const isDebourrage = activeSociete?.type === 'equestre' || 
                           activeSociete?.show_debourrage === true ||
                           modulesActifs.includes('debourrage');
      if (!isDebourrage) return false;
    }

    // Module Attelage - visible uniquement si type equestre ou module activé
    if (item.moduleKey === 'attelage') {
      const isAttelage = activeSociete?.type === 'equestre' || 
                         activeSociete?.show_attelage === true ||
                         modulesActifs.includes('attelage');
      if (!isAttelage) return false;
    }
    
    // Filtrer selon la configuration du tenant (modules optionnels)
    const tenantConfig = tenant?.config || {};
    
    // Modules optionnels - vérifier si activé dans la config du tenant
    if (item.moduleKey) {
      // Expéditeur - module optionnel activable par le client
      if (item.moduleKey === 'expediteur' && tenantConfig.expediteur === false) return false;
      // Élevage - module optionnel activable par le client  
      if (item.moduleKey === 'elevage' && tenantConfig.elevage === false) return false;
    }
    
    return true;
  }).map(item => {
    // Filtrer les sous-menus également
    if (item.submenu) {
      const filteredSubmenu = item.submenu.filter(sub => {
        // Items réservés au propriétaire dans les sous-menus
        if (sub.ownerOnly && !isOwner) return false;

        // Agents propres à une société (ex : CASIA, Distillerie) : visibles seulement dans ce workspace
        if (sub.societeNom && !(activeSociete?.nom || '').toLowerCase().includes(sub.societeNom)) return false;

        // Modules interdits par admin (acces_collaborateurs)
        if (sub.to && isPathBlocked(sub.to)) return false;

        // Filtrer par moduleKey si la société a des modules désactivés
        if (sub.moduleKey && modulesDesactives.length > 0) {
          if (modulesDesactives.includes(sub.moduleKey)) return false;
        }
        
        // Vérifier la config de la société
        if (societeConfig.show_materiel === false && sub.moduleKey === 'materiel') return false;
        if (societeConfig.show_stocks === false && sub.moduleKey === 'stocks') return false;
        if (societeConfig.show_elevage === false && sub.moduleKey === 'elevage') return false;
        
        return true;
      });
      return { ...item, submenu: filteredSubmenu };
    }
    return item;
  });

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="md:hidden fixed top-4 left-4 z-[75] p-2 rounded-lg bg-card border border-border shadow-md"
        data-testid="mobile-menu-button"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Overlay for mobile */}
      {!collapsed && (
        <div
          className="md:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
          onClick={() => setCollapsed(true)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 h-[100dvh] bg-card border-r border-border flex flex-col z-[70] transition-all duration-300',
          collapsed ? 'w-16 -translate-x-full md:translate-x-0' : 'w-64 translate-x-0'
        )}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className={cn(
          'p-4 border-b border-border flex items-center gap-3',
          collapsed && 'justify-center'
        )}>
          {tenant?.branding?.logo_url ? (
            <img 
              src={tenant.branding.logo_url} 
              alt="Logo" 
              className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
            />
          ) : (
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: primaryColor }}
            >
              <Leaf className="w-6 h-6 text-white" />
            </div>
          )}
          {!collapsed && (
            <div className="overflow-hidden">
              <h1 className="font-bold text-foreground truncate">
                {activeSociete?.nom || tenant?.branding?.nom_affiche || 'Bureau Agricole'}
              </h1>
              <p className="text-xs text-muted-foreground">
                {activeSociete?.type === 'equestre' ? 'Centre Équestre' : 
                 activeSociete?.type === 'commerce' ? 'Commercialisation' :
                 activeSociete?.forme_juridique || 'Numérique'}
              </p>
            </div>
          )}
        </div>

        {/* Société Selector */}
        <div className={cn('p-3 border-b border-border', collapsed && 'flex justify-center')}>
          <SocieteSelector collapsed={collapsed} />
        </div>

        {/* Notifications + Historique (hidden on mobile, visible on desktop) */}
        <div className={cn('hidden md:flex p-3 border-b border-border gap-2', collapsed ? 'justify-center flex-col' : 'justify-end')}>
          <ActivityButton />
          <NotificationsDropdown />
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1">
            {filteredNavItems.map((item) => (
              <NavItemWithSubmenu 
                key={item.to} 
                item={item} 
                collapsed={collapsed}
                activeSociete={activeSociete}
              />
            ))}
          </ul>
        </nav>

        {/* Footer - MASQUÉ sur mobile car MobileMoreMenu gère la déconnexion */}
        <div className="hidden md:block p-4 border-t border-border">
          {!collapsed && user && (
            <div className="mb-3 px-2">
              <p className="font-medium text-sm text-foreground truncate">
                {user.prenom}
              </p>
              <p className="text-xs text-muted-foreground capitalize">
                {user.role === 'owner' ? 'Super Admin' : 
                 user.role === 'admin' ? 'Administrateur' : 
                 user.role === 'dirigeant' ? 'Dirigeant' : 
                 user.role === 'collaborateur' ? 'Collaborateur' : 
                 user.role}
              </p>
            </div>
          )}
          
          <div className={cn('flex gap-2', collapsed && 'flex-col')}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/settings')}
              className={cn('flex-1', collapsed && 'px-2')}
              data-testid="nav-settings"
            >
              <Settings className="w-4 h-4" />
              {!collapsed && <span className="ml-2">Paramètres</span>}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className={cn('text-destructive hover:text-destructive min-h-[44px]', collapsed && 'px-2')}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              {!collapsed && <span className="ml-2">Déconnexion</span>}
            </Button>
          </div>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden md:flex absolute -right-3 top-20 w-6 h-6 rounded-full bg-card border border-border items-center justify-center hover:bg-muted"
          data-testid="sidebar-toggle"
        >
          <ChevronLeft className={cn('w-4 h-4 transition-transform', collapsed && 'rotate-180')} />
        </button>
      </aside>
    </>
  );
};

export const Layout = ({ children, collapsed, setCollapsed }) => {
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <main
        className={cn(
          'min-h-screen transition-all duration-300 pt-4 md:pt-0',
          'pb-36 md:pb-0', // Padding for mobile bottom nav + assistant button
          collapsed ? 'md:ml-16' : 'md:ml-64'
        )}
        data-testid="main-content"
      >
        <div className="container-app py-6 px-4 md:px-6">
          <ModuleGuard>{children}</ModuleGuard>
        </div>
      </main>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMoreClick={() => setMoreMenuOpen(true)} />
      
      {/* Mobile More Menu */}
      <MobileMoreMenu isOpen={moreMenuOpen} onClose={() => setMoreMenuOpen(false)} />
      
      {/* Assistant IA - Gros bouton flottant */}
      <AssistantButton />

      {/* Scanner facture fournisseur - Bouton flottant */}
      <ScanFactureButton />
      
      {/* Support Chatbot - Bulle flottante */}
      <SupportChatbot />
    </div>
  );
};
